import * as actionTypes from '../../constants/actionTypes';
import { NEXLA_WEBHOOKS_ENABLED, ORG_FEATURE_FLAG_SETTINGS } from '../../constants/config';
import {
  CONNECTION_CATEGORY_KAFKA,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
  CONNECTOR_CODE_SQL_RUNNER,
  CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
} from '../../constants/connectionCategories';

import has from 'lodash/has';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import forEach from 'lodash/forEach';
import mapValues from 'lodash/mapValues';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import union from 'lodash/union';
import unionBy from 'lodash/unionBy';
import values from 'lodash/values';
import omitBy from 'lodash/omitBy';
import { isObject } from 'lodash';
import flowTypes from 'new/components/flows/types/flowTypes';
import { sortByDisplayOrder } from 'new/utils/displayOrder';
import { isActionEndpoint } from 'new/utils/actionEndpoints';

const initialState = {
  sourcesSupported: [],
  sourcesAllowed: [],
  sinksSupported: [],
  sinksAllowed: [],
  credentialsAllowed: [],
  llmsAllowed: [],
  rerankersAllowed: [],
  fieldsByFlowType: {},
  connectionsByFlowType: {
    [flowTypes.streaming]: { sink: [], source: [] },
    [flowTypes.replication]: { sink: [], source: [] },
    [flowTypes.cdc]: { sink: [], source: [] },
    [flowTypes.elt]: { sink: [], source: [] },
    [flowTypes.spark]: { sink: [], source: [] },
    [flowTypes.in_memory]: { sink: [], source: [] },
    [flowTypes.custom]: { sink: [], source: [] },
    reranker: { sink: [], source: [] },
  },
  soonForFlowTypes: {
    [flowTypes.streaming]: { sink: [], source: [] },
    [flowTypes.replication]: { sink: [], source: [] },
    [flowTypes.cdc]: { sink: [], source: [] },
    [flowTypes.elt]: { sink: [], source: [] },
    [flowTypes.spark]: { sink: [], source: [] },
    [flowTypes.in_memory]: { sink: [], source: [] },
    [flowTypes.custom]: { sink: [], source: [] },
    reranker: { sink: [], source: [] },
  },
  metaInfoById: {},
  connAuthById: {},
  connAuthMeta: {},
  allSourceConfigById: {},
  allSinkConfigById: {},
  sourceConfigById: {},
  sinkConfigById: {},
  llmConfigById: {},
  rerankerConfigById: {},
  commonConnectors: {},
};

export default function (state = initialState, action) {
  switch (action.type) {
    case actionTypes.PAGING_SIGNAL_SUCCESS:
      return setPagingSignalData(state, action.paging);

    case actionTypes.PAGING_SIGNAL_FAILURE:
      return initialState;

    case actionTypes.GET_VENDORS_SUCCESS:
      //case actionTypes.GET_DATA_SOURCES_SUCCESS:
      return setVendorData(state, action);

    case actionTypes.HYDRATE_FROM_LOCAL_STORE:
      return hydrateVendorsFromStore(state, action);

    case actionTypes.GET_FULL_VENDOR_SUCCESS:
      return setFullVendorData(state, action);

    case actionTypes.GET_CREDENTIAL_SUCCESS:
      return setCredentialData(state, action);

    case actionTypes.GET_VENDOR_ENDPOINTS_SUCCESS:
    case actionTypes.GET_VENDOR_ENDPOINT_PARAMS_SUCCESS:
      return setVendorEndpointData(state, action);

    case actionTypes.GET_VENDOR_AUTH_PARAMS_SUCCESS:
      return setVendorAuthData(state, action);

    case actionTypes.SET_COMMON_CONNECTORS_SUCCESS:
      return {
        ...state,
        commonConnectors: action.commonConnectors,
      };

    case actionTypes.SET_COMMON_CONNECTORS_FAILURE:
      return {
        ...state,
        commonConnectors: {},
      };

    default:
      return state;
  }
}

function getFieldsByFlowType(allFields, byFlowType) {
  if (!byFlowType) return allFields;
  return allFields.filter((field) => {
    if (byFlowType.include) {
      return byFlowType.include.includes(field.fieldName || field.name);
    }
    return !byFlowType.exclude.includes(field.fieldName || field.name);
  });
}

function setPagingSignalData(state, pagingData) {
  let {
    sourcesSupported,
    fieldsByFlowType,
    connectionsByFlowType,
    soonForFlowTypes,
    sourcesAllowed,
    sinksSupported,
    sinksAllowed,
    credentialsAllowed,
    llmsAllowed,
    rerankersAllowed,
    metaInfoById,
    connAuthById,
    connAuthMeta,
    allSourceConfigById,
    allSinkConfigById,
    sourceConfigById,
    sinkConfigById,
    llmConfigById,
    rerankerConfigById,
    commonConnectors,
  } = state;

  //convert object of connector keys into an array sorted by connector display name.
  const filteredPagingData = { ...pagingData };

  if (!NEXLA_WEBHOOKS_ENABLED) {
    delete filteredPagingData.nexla_rest;
  }

  let pagingDataArray = map(filteredPagingData, (connData, connKey) => {
    return { key: connKey, ...connData };
  });
  pagingDataArray = sortBy(pagingDataArray, (o) => {
    return o.meta.displayName;
  });

  pagingDataArray = pagingDataArray.map((cc) => {
    let additionalSupportedFlows = { source: [], sink: [] };
    let additionalSoonForFlows = { source: [], sink: [] };
    //Update supported flows for all connectors that are only present in vendors database. this is because we don't want to go update existing vendors in the database.
    switch (cc?.meta?.connectionCategory) {
      case 'templatized_api':
        //any flow type that supports rest connector
        additionalSupportedFlows.source = (() => {
          if (cc?.sourceAllowed) {
            return cc.supportedFlows?.source || ['streaming', 'in_memory'];
          }
          return [];
        })();
        additionalSupportedFlows.sink = (() => {
          if (cc?.sinkAllowed) {
            return cc.supportedFlows?.sink || ['streaming', 'in_memory', 'reranker'];
          }
          return [];
        })();

        break;
      case 'templaized_api_multi':
        //TODO: NEX-13274: for now only limit to elt flows. needs ui for other screens, anywhere `api_multi` base connector is supported.
        additionalSupportedFlows.source = cc?.sourceAllowed ? ['elt'] : [];
        additionalSupportedFlows.sink = [];
        break;
      case 'templatized_script':
      case 'email_script':
        additionalSupportedFlows.source = cc?.sourceAllowed ? ['streaming'] : [];
        additionalSupportedFlows.sink = cc?.sinkAllowed ? ['streaming'] : [];
        break;
      default:
        additionalSupportedFlows.source = [];
        additionalSupportedFlows.sink = [];
    }

    cc.supportedFlows = cc.supportedFlows || { source: [], sink: [] };
    cc.supportedFlows.source = [
      ...new Set([...cc?.supportedFlows.source, ...additionalSupportedFlows.source]),
    ];
    cc.supportedFlows.sink = [
      ...new Set([...cc.supportedFlows.sink, ...additionalSupportedFlows.sink]),
    ];

    return cc;
  });

  forEach(pagingDataArray, (thisConnection, idx) => {
    let { key: connKey, ...connData } = thisConnection;
    metaInfoById = mergeConnectionMetas(metaInfoById, connKey, connData.meta);

    connAuthById = mergeConnectionAuthInfo(connAuthById, connKey, connData.auth);

    connAuthMeta = mergeConnectionAuthMeta(connAuthMeta, connKey, connData.auth);

    let allSourceConfigFields = connData.source?.items || connData.source;
    let sourceConfigFields = getFieldsByFlowType(
      allSourceConfigFields,
      connData.source?.flowTypeItems?.streaming,
    );

    sourceConfigFields = filter(
      sourceConfigFields,
      (field) => field.name !== 'ui.pipeline_selection_enabled',
    );

    let allSinkConfigFields = connData.sink?.items || connData.sink;
    let sinkConfigFields = getFieldsByFlowType(
      allSinkConfigFields,
      connData.sink?.flowTypeItems?.streaming,
    );
    const connFlowTypes =
      connData?.meta?.connectionCategory === 'templatized_api_multi'
        ? connData.supportedFlows || {
            source: [],
            sink: [],
          }
        : connData.supportedFlows || {
            sink: [flowTypes.streaming],
            source: [flowTypes.streaming],
          };

    sourceConfigById = has(connData, 'source')
      ? mergeSourceConfigById(sourceConfigById, connKey, sourceConfigFields)
      : sourceConfigById;

    sinkConfigById = has(connData, 'sink')
      ? mergeSinkConfigById(sinkConfigById, connKey, sinkConfigFields)
      : sinkConfigById;

    const llmConfigFields = connData.llm?.items || connData.llm;
    llmConfigById = has(connData, 'llm')
      ? mergeSinkConfigById(llmConfigById, connKey, llmConfigFields)
      : llmConfigById;

    const rerankerConfigFields = connData.reranker?.items || connData.reranker;
    rerankerConfigById = has(connData, 'reranker')
      ? mergeSinkConfigById(rerankerConfigById, connKey, rerankerConfigFields)
      : llmConfigById;

    allSourceConfigById = has(connData, 'source')
      ? mergeSourceConfigById(allSourceConfigById, connKey, allSourceConfigFields)
      : allSourceConfigById;

    allSinkConfigById = has(connData, 'sink')
      ? mergeSinkConfigById(allSinkConfigById, connKey, allSinkConfigFields)
      : allSinkConfigById;

    sourcesSupported = connData.sourceSupported
      ? union(sourcesSupported, [connKey])
      : sourcesSupported;
    sourcesAllowed = connData.sourceAllowed ? union(sourcesAllowed, [connKey]) : sourcesAllowed;

    sinksSupported = connData.sinkSupported ? union(sinksSupported, [connKey]) : sinksSupported;

    sinksAllowed = connData.sinkAllowed ? union(sinksAllowed, [connKey]) : sinksAllowed;

    llmsAllowed = connData.llmAllowed ? union(llmsAllowed, [connKey]) : llmsAllowed;

    rerankersAllowed = connData.rerankerAllowed
      ? union(rerankersAllowed, [connKey])
      : rerankersAllowed;

    connectionsByFlowType = {
      ...connectionsByFlowType,
    };

    const connFieldsByFlowType = {};

    if (connData.source?.flowTypeItems) {
      Object.keys(connData.source?.flowTypeItems).forEach((type) => {
        connFieldsByFlowType[type] = {
          source: getFieldsByFlowType(
            allSourceConfigFields,
            connData.source.flowTypeItems?.[type],
          ).map((f) => f.name),
        };
      });
    }

    if (connData.sink?.flowTypeItems) {
      Object.keys(connData.sink?.flowTypeItems).forEach((type) => {
        connFieldsByFlowType[type] = {
          ...connFieldsByFlowType[type],
          sink: getFieldsByFlowType(allSinkConfigFields, connData.sink.flowTypeItems?.[type]).map(
            (f) => f.name,
          ),
        };
      });
    }

    fieldsByFlowType = {
      ...fieldsByFlowType,
      [connKey]: {
        ...fieldsByFlowType?.[connKey],
        ...connFieldsByFlowType,
      },
    };

    const soonConnectorsPerFlow = connData.soonForFlows || {
      sink: [],
      source: [],
    };

    soonConnectorsPerFlow.source.forEach((type) => {
      if (soonForFlowTypes[type]?.source && !soonForFlowTypes[type]?.source.includes(connKey)) {
        soonForFlowTypes[type].source.push(connKey);
      } else {
        soonForFlowTypes[type].source = [connKey];
      }
    });

    soonConnectorsPerFlow.sink.forEach((type) => {
      if (soonForFlowTypes[type]?.sink && !soonForFlowTypes[type]?.sink.includes(connKey)) {
        soonForFlowTypes[type].sink.push(connKey);
      } else {
        soonForFlowTypes[type].sink = [connKey];
      }
    });

    connFlowTypes.source.forEach((type) => {
      if (connectionsByFlowType[type]?.source) {
        if (!connectionsByFlowType[type].source.includes(connKey)) {
          connectionsByFlowType[type].source.push(connKey);
        }
      } else {
        connectionsByFlowType[type].source = [connKey];
      }
    });

    connFlowTypes.sink.forEach((type) => {
      if (connectionsByFlowType[type]?.sink) {
        if (!connectionsByFlowType[type].sink.includes(connKey)) {
          connectionsByFlowType[type].sink.push(connKey);
        }
      } else {
        if (!connectionsByFlowType[type]) {
          connectionsByFlowType[type] = { sink: [], source: [] };
        }
        connectionsByFlowType[type].sink = [connKey];
      }
    });

    if (
      (connData.sourceSupported === true ||
        connData.sinkSupported === true ||
        connData.credentialSupported === true) &&
      ['data_map', 'nexla_rest'].indexOf(connKey) === -1 &&
      connData.meta.connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
    ) {
      if (!isEmpty(connData.auth)) {
        credentialsAllowed = union(credentialsAllowed, [connKey]);
      }
    }
  });

  //resort allowed and supported arrays by display name
  sourcesAllowed = sortBy(sourcesAllowed, (s) => {
    const sMeta = metaInfoById[s];
    return sMeta.displayName;
  });

  sourcesSupported = sortBy(sourcesSupported, (s) => {
    const sMeta = metaInfoById[s];
    return sMeta.displayName;
  });

  sinksSupported = sortBy(sinksSupported, (s) => {
    const sMeta = metaInfoById[s];
    return sMeta.displayName;
  });

  sinksAllowed = sortBy(sinksAllowed, (s) => {
    const sMeta = metaInfoById[s];
    return sMeta.displayName;
  });

  //each object in this object has a source & sink array. sort them.
  forEach(connectionsByFlowType, (flowType) => {
    flowType.source = sortBy(flowType.source, (s) => {
      const sMeta = metaInfoById[s];
      return sMeta.displayName;
    });

    flowType.sink = sortBy(flowType.sink, (s) => {
      const sMeta = metaInfoById[s];
      return sMeta.displayName;
    });
  });

  credentialsAllowed = sortBy(credentialsAllowed, (s) => {
    const sMeta = metaInfoById[s];
    return sMeta.displayName;
  });

  return {
    ...state,
    sourcesSupported,
    fieldsByFlowType,
    connectionsByFlowType,
    soonForFlowTypes,
    sourcesAllowed,
    sinksSupported,
    sinksAllowed,
    credentialsAllowed,
    llmsAllowed,
    rerankersAllowed,
    metaInfoById,
    connAuthById,
    connAuthMeta,
    allSourceConfigById,
    allSinkConfigById,
    sourceConfigById,
    sinkConfigById,
    llmConfigById,
    rerankerConfigById,
  };
}

function mergeConnectionMetas(state, connectionType, connectionMeta) {
  let formattedMeta = formatConnectionMeta(connectionType, connectionMeta);
  return { ...state, [connectionType]: formattedMeta };
}

function mergeConnectionAuthInfo(state, connectionType, connectionAuthInfo = {}) {
  const { items, templates, fetchAuthParams } = connectionAuthInfo;
  let formattedAuthInfo = formatInputFields(items);
  return {
    ...state,
    [connectionType]: {
      fetchAuthParams,
      templates: sortByDisplayOrder(
        unionBy(state[connectionType]?.templates || [], templates, 'id'),
      ),
      items: unionBy(formattedAuthInfo, state[connectionType]?.items || [], (item) => {
        if (item.param_id) {
          return item.param_id;
        }
        return item.name;
      }),
    },
  };
}

function mergeConnectionAuthMeta(state, connectionType, connectionAuthInfo = {}) {
  if (!has(connectionAuthInfo, 'items')) {
    return { ...state, [connectionType]: {} };
  }
  return {
    ...state,
    [connectionType]: {
      basic: union(get(state[connectionType], 'basic', []), connectionAuthInfo.basic),
      advanced: union(get(state[connectionType], 'advanced', []), connectionAuthInfo.advanced),
    },
  };
}

function mergeSourceConfigById(state, connectionType, connectionSourceConfig) {
  let formattedSourceConfig = formatInputFields(connectionSourceConfig);

  //check if   if (ORG_FEATURE_FLAG_SETTINGS && ORG_FEATURE_FLAG_SETTINGS?.check_limits) { are true, then hide this if limit_custom_packages is true }
  if (
    ORG_FEATURE_FLAG_SETTINGS &&
    ORG_FEATURE_FLAG_SETTINGS?.check_limits &&
    ORG_FEATURE_FLAG_SETTINGS?.limit_custom_packages
  ) {
    formattedSourceConfig = formattedSourceConfig.map((field) => {
      if (field.name === 'packages') {
        return { ...field, isHiddenField: true };
      }
      return field;
    });
  }

  return {
    ...state,
    [connectionType]: unionBy(state[connectionType], formattedSourceConfig, 'name'),
  };
}

function mergeSinkConfigById(state, connectionType, connectionSinkConfig) {
  let formattedSinkConfig = formatInputFields(connectionSinkConfig);
  return {
    ...state,
    [connectionType]: unionBy(state[connectionType], formattedSinkConfig, 'name'),
  };
}

function formatConnectionMeta(type, metaInfo = {}) {
  if (isEmpty(metaInfo)) {
    return metaInfo;
  }

  const displayName =
    type === CONNECTOR_CODE_SQL_RUNNER
      ? CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME
      : metaInfo.displayName;

  let formattedMeta = merge({}, metaInfo, {
    key: type,
    value: type,
    displayName,
    label: displayName,
    name: displayName,
  });

  return formattedMeta;
}

function formatInputFields(inputFieldMap = {}) {
  const inputIsArray = isArray(inputFieldMap);

  if (isEmpty(inputFieldMap)) {
    return inputIsArray ? inputFieldMap : [];
  }

  let formattedInputFieldMap = map(inputFieldMap, (fieldItem, key) => {
    let newInputData = {};

    newInputData.key = key;
    if (fieldItem.type === 'file') {
      newInputData.inputType = 'file';
    } else if (fieldItem.type === 'frequency') {
      newInputData.inputType = 'frequency';
    } else if (fieldItem.type === 'frequency-type') {
      newInputData.inputType = 'frequency-type';
    } else if (fieldItem.type === 'datetime') {
      newInputData.inputType = 'datetime';
    } else if (fieldItem.type === 'boolean') {
      newInputData.inputType = 'checkbox';
      if (has(fieldItem, 'default')) {
        newInputData.defaultValue = ['yes', 'true', true].includes(fieldItem.default);
      } else {
        newInputData.defaultValue = false;
      }
    } else if (has(fieldItem, 'values')) {
      // If field already has a custom inputType, preserve it
      if (has(fieldItem, 'inputType')) {
        newInputData.inputType = fieldItem.inputType;
      } else {
        newInputData.inputType =
          has(fieldItem, 'can_add_values') && fieldItem.can_add_values === true
            ? 'select-creatable'
            : 'select';
      }
      newInputData.options = map(fieldItem.values, (item) => {
        if (isPlainObject(item) && has(item, 'value') && item.label) {
          return item;
        }

        return { value: item, label: item };
      });
    } else if (fieldItem.type === 'password') {
      newInputData.inputType = 'password';
    } else if (fieldItem.attributes && fieldItem.type === 'string') {
      newInputData.inputType = 'select';
    } else if (fieldItem.attributes && fieldItem.type === 'json') {
      newInputData.inputType = 'attributes-json';
    } else if (fieldItem.type === 'json') {
      newInputData.inputType = 'json';
    } else if (fieldItem.type === 'string_multi' || fieldItem.type === 'string-multi') {
      newInputData.inputType = 'textarea';
    } else if (fieldItem.type === 'button') {
      newInputData.inputType = 'button';
    } else {
      newInputData.inputType = 'text';
    }

    return merge({}, fieldItem, newInputData);
  });

  return formattedInputFieldMap;
}

function hydrateVendorsFromStore(state, action) {
  let { entities, result } = action;
  if (!entities || !result) {
    return { ...state };
  }

  if (has(entities, 'vendors')) {
    return setVendorPagingData(state, entities.vendors);
  }

  return state;
}

function setVendorData(state, action) {
  const {
    entities: { vendors },
  } = action;
  return setVendorPagingData(state, vendors);
}

function setVendorPagingData(state, vendors) {
  const pagingData = {};

  forEach(vendors, (vendor) => {
    const isSource = vendor.config?.isSource || false;
    const isSink = vendor.config?.isSink || false;
    const isLLM = vendor.config?.isLLM || false;
    const isReranker = vendor.config?.isReranker || false;
    const isCredential = vendor.config?.isCredential || false;
    const templates = state.connAuthById[vendor.name]?.templates || [];
    pagingData[vendor.name] = {
      meta: getVendorMeta(vendor, state),
      auth: {
        ...state.connAuthById[vendor.name],
        items: state.connAuthById[vendor.name]?.items || {},
        basic: state.connAuthById[vendor.name]?.basic || [],
        advanced: state.connAuthById[vendor.name]?.advanced || [],
        templates,
      },
      source: [],
      sink: [],
      llm: [],
      reranker: [],
      sourceSupported: isSource,
      sourceAllowed: isSource,
      sinkSupported: isSink,
      sinkAllowed: isSink,
      credentialSupported: isCredential,
      llmAllowed: isLLM,
      rerankerAllowed: isReranker,
      supportedFlows: vendor.config?.supportedFlows,
    };

    //add soonForFlows if vendor.config has soonForFlows, otherwise skip it
    if (vendor.config?.soonForFlows) {
      pagingData[vendor.name].soonForFlows = vendor.config.soonForFlows;
    }
    //add supportedFlows if vendor.config has supportedFlows, otherwise skip it
    if (vendor.config?.supportedFlows) {
      pagingData[vendor.name].supportedFlows = vendor.config.supportedFlows;
    }
  });

  return setPagingSignalData(state, pagingData);
}

function setVendorEndpointData(state, action, vendor_id) {
  const {
    entities: { vendorEndpoints, vendorEndpointParams },
  } = action;
  const { metaInfoById } = state;
  const endpointsByVendorId =
    vendor_id && vendorEndpoints
      ? { [vendor_id]: Object.values(vendorEndpoints) }
      : groupBy(vendorEndpoints, 'vendor');

  // pagingParams[vendorId][resourceType] = [<template config properties>]
  const pagingParams = {};

  // add vendor_endpoint_id entries
  forEach(endpointsByVendorId, (endpoints, key) => {
    const vendorId = vendor_id || key;

    if (!pagingParams[vendorId]) {
      pagingParams[vendorId] = {};
    }

    const endpointsWithResourceType = map(sortByDisplayOrder(endpoints), (endpoint) => ({
      ...endpoint,
      resource_type: endpoint.config?.isLLM ? 'LLM' : endpoint.resource_type,
    }));
    const endpointsByResourceType = groupBy(endpointsWithResourceType, 'resource_type');
    pagingParams[vendorId] = mapValues(endpointsByResourceType, (endpoints) => [
      {
        name: 'vendor_endpoint_id',
        display_name: 'Endpoint',
        description: 'Endpoint',
        type: 'int',
        required: true,
        // Values are already in display order; keep select components from re-sorting by label
        unsorted: true,
        values: map(endpoints, (endpoint) => ({
          label: endpoint.display_name,
          description: endpoint.description,
          value: endpoint.id,
          // Action endpoints are hidden from ETL source forms (see new/utils/actionEndpoints)
          is_action: isActionEndpoint(endpoint),
        })),
      },
    ]);
  });

  // add rest of the entries
  const endpointParamsByGlobal = groupBy(vendorEndpointParams, (param) => !!param.global);

  // globalPagingParams[resourceType] = [<template config properties>]
  const globalPagingParams = mapValues(
    groupBy(endpointParamsByGlobal[true], 'resource_type'),
    (endpointParams) => map(endpointParams, getPagingParamFromVendorParam),
  );

  forEach(endpointParamsByGlobal[false], (endpointParam) => {
    const vendorEndpoint = endpointParam.vendor_endpoint;
    const endpointId = isObject(vendorEndpoint) ? vendorEndpoint.id : vendorEndpoint;
    if (!vendor_id && !has(vendorEndpoints[endpointId], 'vendor')) {
      //ERROR IN Vendor endpoint entry. skip this endpoint.
      return;
    }
    const vendorId = vendor_id || vendorEndpoints[endpointId].vendor;
    const resourceType = endpointParam.config?.isLLM ? 'LLM' : endpointParam.resource_type;
    if (!['SOURCE', 'SINK', 'LLM'].includes(resourceType)) {
      //safety check to root out poorly structured entries that are not meant for ui.
      return;
    }
    const pagingParam = getPagingParamFromVendorParam(endpointParam, endpointId);

    pagingParams[vendorId][resourceType].push(pagingParam);
  });

  const globalSourceParams = globalPagingParams.SOURCE || [];
  const globalSinkParams = globalPagingParams.SINK || [];
  const pagingData = {};

  function compareParams(param1, param2) {
    // sort resource params by endpoint, order
    if (
      get(param1, 'dependsOn.name') === 'vendor_endpoint_id' &&
      get(param2, 'dependsOn.name') === 'vendor_endpoint_id' &&
      param1.dependsOn.value !== param2.dependsOn.value
    ) {
      return param1.dependsOn.value - param2.dependsOn.value;
    }

    return get(param1, 'order', 0) - get(param2, 'order', 0);
  }

  forEach(metaInfoById, (meta, name) => {
    const { vendorId } = meta;

    if (!vendorId) {
      return;
    }
    let globalSourceParamsTemp = globalSourceParams;
    // For Connection category KAFKA type, need to omit start.cron
    if (meta.connectionCategory === CONNECTION_CATEGORY_KAFKA) {
      globalSourceParamsTemp = values(omitBy(globalSourceParams, { name: 'start.cron' }));
    }

    const sourceParams = filter(
      get(pagingParams, `[${vendorId}].SOURCE`, [])
        .concat(globalSourceParamsTemp)
        .sort(compareParams),
      (param) =>
        !(
          meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT &&
          param.exclude_from_scripts
        ),
    );
    const sinkParams = filter(
      get(pagingParams, `[${vendorId}].SINK`, []).concat(globalSinkParams).sort(compareParams),
      (param) =>
        !(
          meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT &&
          param.exclude_from_scripts
        ),
    );
    const llmParams = get(pagingParams, `[${vendorId}].LLM`, []).sort(compareParams);
    const rerankerParams = get(pagingParams, `[${vendorId}].RERANKER`, []).sort(compareParams);
    // const sourceSupported = !isEmpty(sourceParams);
    // const sinkSupported = !isEmpty(sinkParams);

    const templates = state.connAuthById?.[name]?.templates || [];
    pagingData[name] = {
      ...state.pagingData?.[name],
      meta,
      // sourceSupported: sourceSupported,
      // sourceAllowed: sourceSupported,
      // sinkSupported: sinkSupported,
      // sinkAllowed: sinkSupported,
      auth: {
        ...state.connAuthById?.[name],
        templates,
        items: state.connAuthById?.[name]?.items || {},
        basic: state.connAuthById?.[name]?.basic || [],
        advanced: state.connAuthById?.[name]?.advanced || [],
      },
      source: sourceParams,
      sink: sinkParams,
      llm: llmParams,
      reranker: rerankerParams,
    };
  });

  return setPagingSignalData(state, pagingData);
}

function setVendorAuthData(state, action, vendId) {
  const {
    entities: { vendors: entitiyVendors, vendorAuthParams, vendorAuthTemplates },
    requestInfo,
  } = action;

  const vendors = entitiyVendors || requestInfo?.vendors;

  const authParamsByGlobal = groupBy(vendorAuthParams, (param) => !!param.global);

  // globalPagingParams = {<template config properties>}
  const globalPagingParams = {};

  forEach(authParamsByGlobal[true], (authParam) => {
    globalPagingParams[authParam.id] = getPagingParamFromVendorParam(authParam);
  });

  // pagingParams[vendorId] = {<template config properties>}
  const pagingParams = {};

  forEach(authParamsByGlobal[false], (authParam) => {
    const vendorId =
      vendId || (typeof authParam.vendor === 'object' ? authParam.vendor.id : authParam.vendor);

    if (!pagingParams[vendorId]) {
      pagingParams[vendorId] = {};
    }

    pagingParams[vendorId][authParam.id] = getPagingParamFromVendorParam(authParam);
  });

  const pagingData = {};

  forEach(vendors, (vendor) => {
    const authItems = merge({}, globalPagingParams, pagingParams[vendor.id]);
    const sortedNames = map(sortBy(values(authItems), 'order'), 'name');

    pagingData[vendor.name] = {
      meta: getVendorMeta(vendor, state),
      auth: {
        ...state.connAuthById[vendor.name],
        templates: sortByDisplayOrder(Object.values(vendorAuthTemplates || {})),
        items: unionBy(Object.values(authItems), state.connAuthById[vendor.name]?.items, (item) => {
          if (item.param_id) {
            return item.param_id;
          }
          return item.name;
        }),
        basic: union(state.connAuthMeta[vendor.name]?.basic, sortedNames),
        advanced: state.connAuthMeta[vendor.name]?.advanced,
      },
    };
  });

  return setPagingSignalData(state, pagingData);
}

function setFullVendorData(state, action) {
  state = setVendorData(state, action);
  state = setVendorEndpointData(state, action, action.result);
  state = setVendorAuthData(state, action, action.result);
  return state;
}

function setCredentialData(state, action) {
  const vendors = action?.entities?.vendors;
  const vendorId = vendors ? Object.keys(vendors)?.[0] : undefined;
  state = setVendorData(state, action);
  state = setVendorEndpointData(state, action, vendorId);
  state = setVendorAuthData(state, action, vendorId);
  return state;
}

function getPagingParamFromVendorParam(vendorParam, endpointId = null) {
  const pagingParam = {
    name: vendorParam.name,
    display_name: vendorParam.display_name,
    description: vendorParam.description,
    type: vendorParam.data_type,
    required: true,
    order: vendorParam.order,
    param_id: vendorParam.id,
    ...vendorParam.config,
  };

  if (pagingParam.param_ref && pagingParam.isHiddenField !== false) {
    pagingParam.isHiddenField = true;
  }

  if (endpointId) {
    pagingParam.dependsOn = {
      allOf: [
        {
          name: 'vendor_endpoint_id',
          value: endpointId,
        },
      ],
    };

    const {
      config: { dependsOn },
    } = vendorParam;

    if (dependsOn) {
      if (dependsOn.allOf) {
        pagingParam.dependsOn.allOf = [...pagingParam.dependsOn.allOf, ...dependsOn.allOf];
      } else {
        pagingParam.dependsOn.allOf = [...pagingParam.dependsOn.allOf, dependsOn];
      }
    }
  }

  if (!isEmpty(vendorParam.allowed_values)) {
    pagingParam.values = vendorParam.allowed_values;
  }

  return pagingParam;
}

function getVendorMeta(vendor, state) {
  const { metaInfoById } = state;
  const vendorConfig = has(vendor, 'config') ? vendor.config : {};

  const connectionCategory = has(vendorConfig, 'connectionCategory')
    ? vendorConfig.connectionCategory
    : vendor.name === 'email'
      ? CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT
      : vendor.connection_type === 'script'
        ? CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
        : CONNECTION_CATEGORY_TEMPLATIZED_API;

  // don't overwrite meta of connections defined in paging when their vendor
  // auth_parameters are loaded
  if (
    metaInfoById &&
    metaInfoById[vendor.name] &&
    metaInfoById[vendor.name].connectionCategory !== connectionCategory
  ) {
    const existing = metaInfoById[vendor.name];
    if (vendor.name === CONNECTOR_CODE_SQL_RUNNER) {
      return {
        ...existing,
        displayName: CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
        name: CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
        label: CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME,
      };
    }
    return existing;
  }

  const displayName =
    vendor.name === CONNECTOR_CODE_SQL_RUNNER
      ? CONNECTOR_CODE_SQL_RUNNER_DISPLAY_NAME
      : vendor.display_name;

  return {
    displayName,
    icon: vendor.logo,
    iconSm: vendor.small_logo,
    connectionType: vendor.connection_type,
    industryCategory: has(vendorConfig, 'industryCategory')
      ? vendorConfig.industryCategory
      : 'Miscellaneous',
    connectionTypeCategory:
      metaInfoById &&
      metaInfoById[vendor.connection_type] &&
      metaInfoById[vendor.connection_type].connectionCategory,
    connectionCategory,
    vendorId: vendor.id,
    allowSinkAutoMapping: !!(
      metaInfoById &&
      metaInfoById[vendor.name] &&
      metaInfoById[vendor.name].allowSinkAutoMapping
    ),
  };
}
